import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { StepperOption } from '../../../../../ui-components/lib/stepper/stepper-options.interface';
import { requestDetailsEmptyData } from '../request.mockdata';
import { SideMenuAssigneeDropdownViewComponent } from '../../../../pages/employee-flow/request/side-menu-assignee-dropdown-view/side-menu-assignee-dropdown-view.component';
import { UserList } from '../../../../common/models/user.model';
import { OverlayLoaderComponent } from '../../../../common/components/overlay-loader/overlay-loader.component';
import { RequestDetailsHeaderComponent } from '../../../../common/components/request-details-header/request-details-header.component';
import { RequestComponentService } from '../services/request-component/request-component.service';
import { switchMap, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HoldResumeModalComponent } from '../request-actions-modals/hold-resume-modal/hold-resume-modal.component';
import { RequestActionEnum } from '../../../../common/enums/request-action.enum';
import { AppPermissions } from '../../../../common/constants/app-permissions';
import { PermissionsService } from '../../../../common/services/permissions/permissions.service';
import {
  AccessKeyResponse,
  WorkflowAccessKeyService,
} from '../../../../common/services/workflow-access-key/workflow-access-key.service';
import { DiscussionBase } from '../base/discussion-base';
import { isVendorPortalUrl } from '../../../../common/helpers/portal/is-vendor-portal-url';
import { RequestViewActionsComponent } from '../request-view-actions/request-view-actions.component';
import { CollaboratorService } from '../services/collaborator/collaborator.service';
import { REQUEST_CONTEXT_ADAPTER } from '../adapters/request-context.adapter';
import { EmployeeRequestContextAdapter } from '../adapters/employee-request-context.adapter';
import { RequestDetailsResponse } from '../../../../common/models/request-details.model';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import { StepStatusEnum } from '../../../../common/enums/step-status';
import { AppStorage } from '../../../../common/enums/app-storage';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { Option } from '../../../../common/models/option.model';
import { ApiResponse } from '../../../../common/models/api-response.model';
import { ActionClassPipe } from '../action-class/action-class.pipe';
import { AlertInlineComponent } from '../../../../../ui-components/lib/alert-inline/alert-inline.component';
import { ButtonComponent } from '../../../../../ui-components/lib/button/button.component';
import { StepperComponent } from '../../../../../ui-components/lib/stepper/stepper.component';
import { ModalServiceService } from '../../../../../ui-components/lib/modal/modal-service.service';
import { AssigneeTypePipe } from '../../../../../ui-components/pipes/assignee-type/assignee-type.pipe';
import { AlertData } from '../../../../../ui-components/lib/alert-inline/alert-data.interface';
import {
  Action,
  RequestFormGroup,
  RequestFromBuilderResponse,
} from '../../../../../form-builder/interface/request-form-builder.model';
import { FormBuilderComponent } from '../../../../../form-builder/lib/form-builder/form-builder.component';
import { ChevronDownIconComponent } from '@kuiper/icons';

@Component({
  selector: 'lib-request-view',
  imports: [
    ReactiveFormsModule,
    RequestDetailsHeaderComponent,
    StepperComponent,
    TranslocoPipe,
    FormBuilderComponent,
    OverlayLoaderComponent,
    SideMenuAssigneeDropdownViewComponent,
    AssigneeTypePipe,
    RequestViewActionsComponent,
    HoldResumeModalComponent,
    ButtonComponent,
    ActionClassPipe,
    AlertInlineComponent,
    ChevronDownIconComponent,
  ],
  templateUrl: './request-view.component.html',
  styleUrl: './request-view.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RequestViewComponent
  extends DiscussionBase
  implements OnInit, OnDestroy
{
  activatedRoute = inject(ActivatedRoute);
  requestComponentService = inject(RequestComponentService);
  router = inject(Router);
  transloco = inject(TranslocoService);
  collaboratorService = inject(CollaboratorService);
  private readonly requestContextAdapter =
    inject(REQUEST_CONTEXT_ADAPTER, { optional: true }) ??
    inject(EmployeeRequestContextAdapter);
  protected readonly showCollaboratorsSection = computed(
    () =>
      this.requestContextAdapter.showCollaboratorsSection() &&
      !isVendorPortalUrl(this.router.url)
  );
  featureFlagsService = inject(FeatureFlagsService);
  isRequestHeaderUiEnhancementEnabled = signal<boolean>(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.workflow_header_ui_enhancement
    )
  );
  workflowAccessKeyService = inject(WorkflowAccessKeyService);
  modalService = inject(ModalServiceService);
  permissionsService = inject(PermissionsService);
  appPermissions = AppPermissions;

  requestDetailsResponse = signal<RequestDetailsResponse>({
    data: requestDetailsEmptyData,
  });
  stepperOption = signal<StepperOption[]>([]);
  /******** ui ******/
  isLoading = signal<boolean>(false);
  isStepperCollapsed = signal<boolean>(true);
  requestType = signal<string>(RequestTypeEnum.VIEW);
  isDisabled = signal<boolean>(false);

  /************** collaborators **************/
  isLoadingCollaborators = signal<boolean>(false);
  totalCollaboratorsPages = signal<number>(0);
  collaboratorsCurrentPage = signal<number>(1);
  totalCollaboratorsItems = signal<number>(0);
  collaborators = signal<Option[]>([]);
  rawCollaborators = signal<Option[]>([]);
  /************** formBuilder **************/
  requestFormGroups = signal<RequestFormGroup[]>([]);
  requestForm = new FormGroup({});

  canDeactivate = signal<boolean>(true);

  /******************* modals ********************/
  holdModalTemplate = viewChild<TemplateRef<any>>('holdModalTemplate');
  resumeModalTemplate = viewChild<TemplateRef<any>>('resumeModalTemplate');

  // actions
  onHoldAction = signal<Action>({
    value: RequestActionEnum.HOLD,
    label: this.transloco.translate('request_screen_on_hold_button'),
    color: '',
  });

  resumeAction = signal<Action>({
    value: RequestActionEnum.RESUME,
    label: this.transloco.translate('request_screen_resume_button'),
    color: 'primary',
  });

  holdResumeAction = signal<Action>({
    value: '',
    label: '',
    color: '',
  });

  // hold alert
  showHoldAlert = signal<boolean>(false);
  holdAlertData = signal<AlertData>({
    title: this.transloco.translate('request_screen_hold_alert_title'),
    description: this.transloco.translate(
      'request_screen_hold_alert_description'
    ),
    status: 'warning',
  });

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();
    effect(() => {
      if (!this.modalService.modalContent().isOpen) {
        this.canDeactivate.set(true);
      }
    });
  }

  ngOnInit(): void {
    this.handleActivatedRoute();
  }

  handleActivatedRoute() {
    this.subscription.add(
      this.activatedRoute.params.subscribe(params => {
        this.flowId.set(+params['flowId']);
        if (this.flowId()) {
          this.getAuthorizationKey();
          this.handleRefreshAuthorizationKey();
        } else {
          history.back();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.pusherService.unsubscribe(this.channelName());
    this.isPusherChannelActive.set(false);
    sessionStorage.removeItem(AppStorage.accessKey);
  }

  getAuthorizationKey() {
    this.subscription.add(
      this.workflowAccessKeyService
        .getFlowAuthorizationKey(this.flowId())
        .subscribe({
          next: (res: AccessKeyResponse) => {
            this.handleAuthorizationKeySuccess(res);
          },
        })
    );
  }

  handleAuthorizationKeySuccess(res: AccessKeyResponse) {
    const accessKey = res.data.access_key;
    sessionStorage.setItem(AppStorage.accessKey, accessKey);
    this.handleFlowIdExist(this.flowId().toString());
    this.listenToVisibilityChanges();

    // Check if discussion should be opened based on query params
    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.handleOpenedDiscussionFromQueryParams(queryParams);
  }

  handleRefreshAuthorizationKey() {
    timer(
      this.workflowAccessKeyService.refreshingAccessKeyTime,
      this.workflowAccessKeyService.refreshingAccessKeyTime
    )
      .pipe(
        switchMap(() =>
          this.workflowAccessKeyService.refreshFlowAuthorizationKey(
            this.flowId()
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  handleFlowIdExist(flowId: string) {
    this.getFlowDetails(flowId);
    this.getFlowFormSections(flowId);
    if (this.showCollaboratorsSection()) {
      this.getAssignedCardList({ currentPage: 1, search: '' });
    }
  }

  getFlowFormSections(id: string) {
    this.subscription.add(
      this.requestService.getFlowFormSections(id).subscribe({
        next: (res: RequestFromBuilderResponse) => {
          this.requestFormGroups.set(res.data.sections);
          this.requestForm = this.requestComponentService.buildRequestForm(
            this.requestFormGroups()
          );
        },
      })
    );
  }

  getFlowDetails(flowId: string) {
    this.isLoading.set(true);
    this.subscription.add(
      this.requestService.getFlowDetails(flowId).subscribe({
        next: (res: any) => {
          this.requestDetailsResponse.set(res);

          this.flowId.set(res.data.id);
          this.getFlowStepsDetails(this.flowId());
          this.subscribeToNewComment();
          this.isPusherChannelActive.set(true);
          this.showUnreadMessagesIndicator.set(res.data.has_unread_comments);

          this.requestDetailsResponse.update(x => {
            x.data.positions?.forEach(p => {
              p.state = this.requestService.getPositionState(p);
            });
            return x;
          });

          this.stepperOption.set(
            this.requestComponentService.drawStepper(
              this.requestDetailsResponse()
            )
          );

          if (
            this.permissionsService.checkPermission(
              this.appPermissions.workflow.hold,
              this.requestDetailsResponse().data.permissions
            )
          ) {
            this.setHoldResumeAction();
          }

          this.isLoading.set(false);
        },
        error: (error: any) => {
          if (error.status === 403) {
            this.router.navigate([this.tenantService.tenantPath('')]);
          }
          this.isLoading.set(false);
        },
      })
    );
  }

  getFlowStepsDetails(flowId: string | number) {
    this.subscription.add(
      this.requestService.getFlowStepsDetails(flowId).subscribe({
        next: (res: any) => {
          this.requestDetailsResponse.set(res);

          this.requestDetailsResponse.update(x => {
            x.data.positions?.forEach(p => {
              p.state = this.requestService.getPositionState(p);
            });
            return x;
          });
        },
        error: (error: any) => {
          if (error.status === 403) {
            this.router.navigate([this.tenantService.tenantPath('')]);
          }
        },
      })
    );
  }

  getAssignedCardList(event: { currentPage: number; search: string }) {
    if (!this.showCollaboratorsSection()) {
      return;
    }

    this.collaboratorsCurrentPage.set(event.currentPage);
    this.isLoadingCollaborators.set(true);

    this.subscription.add(
      this.collaboratorService
        .getCollaborators(
          this.flowId()?.toString() ?? '',
          this.collaboratorsCurrentPage(),
          event.search
        )
        .subscribe({
          next: response =>
            this.handleCollaboratorsSuccessResponse(response, event.search),
          error: () => this.isLoadingCollaborators.set(false),
        })
    );
  }

  handleCollaboratorsSuccessResponse(
    response: ApiResponse<UserList>,
    search: string
  ) {
    this.collaborators.set(
      response.data.map((collaborator: any) => ({
        value: collaborator.id,
        label: collaborator.name,
        image: collaborator.image,
        job_title: collaborator.work_info?.job_title?.name ?? '',
      })) as unknown as Option[]
    );
    if (search === '') {
      this.rawCollaborators.set(this.collaborators());
    }
    this.totalCollaboratorsPages.set(response.meta.last_page);
    this.totalCollaboratorsItems.set(response.meta.total);
    this.isLoadingCollaborators.set(false);
  }

  cancel() {
    history.back();
  }

  handleTakenAction(action: string) {
    switch (action) {
      case RequestActionEnum.HOLD:
        this.openHoldModal();
        break;
      case RequestActionEnum.RESUME:
        this.openResumeModal();
        break;
    }
  }

  setHoldResumeAction() {
    if (
      this.requestDetailsResponse().data?.state?.value ===
      StepStatusEnum.INPROGRESS
    ) {
      this.showHoldAlert.set(false);
      this.holdResumeAction.set(this.onHoldAction());
    } else if (
      this.requestDetailsResponse().data?.state?.value ===
      StepStatusEnum.ON_HOLD
    ) {
      this.showHoldAlert.set(true);
      this.holdResumeAction.set(this.resumeAction());
    }
  }

  openHoldModal() {
    this.canDeactivate.set(false);
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: this.transloco.translate('request_screen_hold_modal_title'),
      customizeContent: this.holdModalTemplate(),
      width: '45.375',
      canNotCloseFromOutside: true,
    });
  }

  openResumeModal() {
    this.canDeactivate.set(false);
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: true,
      title: this.transloco.translate('request_screen_resume_modal_title'),
      customizeContent: this.resumeModalTemplate(),
      width: '45.375',
      canNotCloseFromOutside: true,
    });
  }

  holdRequest(actionData: any) {
    if (this.isDisabled()) {
      return;
    } else {
      this.isDisabled.set(true);
    }

    const payload: any = {
      comment: { en: actionData.comment },
    };

    this.subscription.add(
      this.requestService.holdRequest(payload, this.flowId()).subscribe({
        next: () => {
          this.handleHoldResumeSuccess();
        },
        error: () => {
          this.isDisabled.set(false);
        },
      })
    );
  }

  resumeRequest(actionData: any) {
    if (this.isDisabled()) {
      return;
    } else {
      this.isDisabled.set(true);
    }

    const payload: any = {
      comment: { en: actionData.comment ? actionData.comment : null },
    };

    this.subscription.add(
      this.requestService.resumeRequest(payload, this.flowId()).subscribe({
        next: () => {
          this.handleHoldResumeSuccess();
        },
        error: () => {
          this.isDisabled.set(false);
        },
      })
    );
  }

  handleHoldResumeSuccess() {
    this.isDisabled.set(false);
    this.closeModal();
    this.cancel();
  }

  closeModal() {
    this.canDeactivate.set(true);
    this.modalService.modalContent.set({
      isOpen: false,
    });
  }
}
